/**
 * AEB功能开关-批量设置表单
 */
import React, { useImperativeHandle, forwardRef } from 'react';
import { Form, InputNumber, Row, Col, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { formSearchGutter } from '@/constants/common';
import { filterFormValues } from '@/utils/useFormToJsonString';

export interface AebFunctionSwitchWriteFormRef {
  getSubmitString: () => Promise<string | null>;
}

const AebFunctionSwitchWriteForm = forwardRef<AebFunctionSwitchWriteFormRef, {}>((props, ref) => {
  const [form] = Form.useForm();

  // 带说明的标签组件
  const LabelWithTooltip = ({ label, tooltip }: { label: string; tooltip: string }) => (
    <span>
      {label}{' '}
      <Tooltip title={tooltip}>
        <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
      </Tooltip>
    </span>
  );

  // 导出方法：获取表单数据的JSON字符串
  useImperativeHandle(ref, () => ({
    getSubmitString: async () => {
      const values = await form.validateFields();
      return filterFormValues(values);
    }
  }));

  return (
    <Form form={form}>
      <Row gutter={formSearchGutter}>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="工作模式" tooltip="取值范围：0~15，0~3bit；0：无效；1：production；2：debuging；其他预留" />}
            name="working_mode"
          >
            <InputNumber placeholder="请输入工作模式" style={{ width: '100%' }} precision={0} min={0} max={15} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="感知类型配置" tooltip="取值范围：0~15，4~7bit；0：无效；1：单V；2：单Radar；3：1V1R融合；其他预留" />}
            name="perceptionTypeConfig"
          >
            <InputNumber placeholder="请输入感知类型配置" style={{ width: '100%' }} precision={0} min={0} max={15} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="车型配置" tooltip="取值范围：0~255，1：GEELY；2：CHERY；待补全" />}
            name="vehicleTypeConfigure"
          >
            <InputNumber placeholder="请输入车型配置" style={{ width: '100%' }} precision={0} min={0} max={255} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={formSearchGutter}>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="细分车型配置" tooltip="取值范围：0~255，1-1：GEELY_LE25；1-2：GEELY_E51；待补全" />}
            name="vehicleSubTypeConfigure"
          >
            <InputNumber placeholder="请输入细分车型配置" style={{ width: '100%' }} precision={0} min={0} max={255} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="LCC功能配置" tooltip="取值范围：0~1，0bit；0：功能关闭；1：功能开启" />}
            name="lccEnable"
          >
            <InputNumber placeholder="请输入LCC功能配置" style={{ width: '100%' }} precision={0} min={0} max={1} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="LDP功能配置" tooltip="取值范围：0~1，1bit；0：功能关闭；1：功能开启" />}
            name="ldpEnable"
          >
            <InputNumber placeholder="请输入LDP功能配置" style={{ width: '100%' }} precision={0} min={0} max={1} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={formSearchGutter}>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="LDW功能配置" tooltip="取值范围：0~1，2bit；0：功能关闭；1：功能开启" />}
            name="ldwEnable"
          >
            <InputNumber placeholder="请输入LDW功能配置" style={{ width: '100%' }} precision={0} min={0} max={1} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="ACC功能配置" tooltip="取值范围：0~1，3bit；0：功能关闭；1：功能开启" />}
            name="accEnable"
          >
            <InputNumber placeholder="请输入ACC功能配置" style={{ width: '100%' }} precision={0} min={0} max={1} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="AEB功能配置" tooltip="取值范围：0~1，4bit；0：功能关闭；1：功能开启" />}
            name="aebEnable"
          >
            <InputNumber placeholder="请输入AEB功能配置" style={{ width: '100%' }} precision={0} min={0} max={1} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={formSearchGutter}>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="FCW功能配置" tooltip="取值范围：0~1，5bit；0：功能关闭；1：功能开启" />}
            name="fcwEnable"
          >
            <InputNumber placeholder="请输入FCW功能配置" style={{ width: '100%' }} precision={0} min={0} max={1} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="HMW功能配置" tooltip="取值范围：0~1，6bit；0：功能关闭；1：功能开启" />}
            name="hmwEnable"
          >
            <InputNumber placeholder="请输入HMW功能配置" style={{ width: '100%' }} precision={0} min={0} max={1} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="DMS功能配置" tooltip="取值范围：0~1，7bit；0：功能关闭；1：功能开启" />}
            name="dmdEnable"
          >
            <InputNumber placeholder="请输入DMS功能配置" style={{ width: '100%' }} precision={0} min={0} max={1} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={formSearchGutter}>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="左侧BSD功能配置" tooltip="取值范围：0~1，8bit；0：功能关闭；1：功能开启" />}
            name="bsdLeftEnable"
          >
            <InputNumber placeholder="请输入左侧BSD功能配置" style={{ width: '100%' }} precision={0} min={0} max={1} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="右侧BSD功能配置" tooltip="取值范围：0~1，9bit；0：功能关闭；1：功能开启" />}
            name="bsdRightEnable"
          >
            <InputNumber placeholder="请输入右侧BSD功能配置" style={{ width: '100%' }} precision={0} min={0} max={1} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="RVC功能配置" tooltip="取值范围：0~1，10bit；0：功能关闭；1：功能开启" />}
            name="rvcEnable"
          >
            <InputNumber placeholder="请输入RVC功能配置" style={{ width: '100%' }} precision={0} min={0} max={1} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={formSearchGutter}>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="限速提醒功能配置" tooltip="取值范围：0~1，0bit；0：功能关闭；1：功能开启" />}
            name="slifEnable"
          >
            <InputNumber placeholder="请输入限速提醒功能配置" style={{ width: '100%' }} precision={0} min={0} max={1} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="车速交通标志识别功能配置" tooltip="取值范围：0~1，1bit；0：功能关闭；1：功能开启" />}
            name="sliEnable"
          >
            <InputNumber placeholder="请输入车速交通标志识别功能配置" style={{ width: '100%' }} precision={0} min={0} max={1} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="AEB功能灵敏度配置" tooltip="取值范围：1~10，0~3bit；数值越大，AEB制动越积极、频繁" />}
            name="sensitivityAeb"
          >
            <InputNumber placeholder="请输入AEB功能灵敏度配置" style={{ width: '100%' }} precision={0} min={1} max={10} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={formSearchGutter}>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="FCW功能灵敏度配置" tooltip="取值范围：1~10，4~7bit；数值越大，FCW制动越积极、频繁" />}
            name="sensitivityFcw"
          >
            <InputNumber placeholder="请输入FCW功能灵敏度配置" style={{ width: '100%' }} precision={0} min={1} max={10} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="HMW功能灵敏度配置" tooltip="取值范围：1~10，0~3bit；数值越大，HMW制动越积极、频繁" />}
            name="sensitivityHmw"
          >
            <InputNumber placeholder="请输入HMW功能灵敏度配置" style={{ width: '100%' }} precision={0} min={1} max={10} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="ACC功能灵敏度配置" tooltip="取值范围：1~10，4~7bit；数值越大，ACC反应越灵敏" />}
            name="sensitivityAcc"
          >
            <InputNumber placeholder="请输入ACC功能灵敏度配置" style={{ width: '100%' }} precision={0} min={1} max={10} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={formSearchGutter}>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="LDW功能灵敏度配置" tooltip="取值范围：1~10，0~3bit；数值越大，LDW制动越积极、频繁" />}
            name="sensitivityLdw"
          >
            <InputNumber placeholder="请输入LDW功能灵敏度配置" style={{ width: '100%' }} precision={0} min={1} max={10} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="LDP功能灵敏度配置" tooltip="取值范围：1~10，4~7bit；数值越大，LDP触发越积极、频繁" />}
            name="sensitivityLdp"
          >
            <InputNumber placeholder="请输入LDP功能灵敏度配置" style={{ width: '100%' }} precision={0} min={1} max={10} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="LCC功能灵敏度配置" tooltip="取值范围：1~10，0~3bit；数值越大，LCC制动越积极、频繁" />}
            name="sensitivityLcc"
          >
            <InputNumber placeholder="请输入LCC功能灵敏度配置" style={{ width: '100%' }} precision={0} min={1} max={10} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={formSearchGutter}>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="DMS功能灵敏度配置" tooltip="取值范围：1~10，4~7bit；数值越大，DMS制动越积极、频繁" />}
            name="sensitivityDms"
          >
            <InputNumber placeholder="请输入DMS功能灵敏度配置" style={{ width: '100%' }} precision={0} min={1} max={10} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="左侧BSD功能灵敏度配置" tooltip="取值范围：1~10，0~3bit；数值越大，BSD制动越积极、频繁" />}
            name="sensitivityBsdLeft"
          >
            <InputNumber placeholder="请输入左侧BSD功能灵敏度配置" style={{ width: '100%' }} precision={0} min={1} max={10} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="右侧BSD功能灵敏度配置" tooltip="取值范围：1~10，4~7bit；数值越大，BSD制动越积极、频繁" />}
            name="sensitivityBsdRight"
          >
            <InputNumber placeholder="请输入右侧BSD功能灵敏度配置" style={{ width: '100%' }} precision={0} min={1} max={10} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={formSearchGutter}>
        <Col span={8}>
          <Form.Item
            label={<LabelWithTooltip label="RVC功能灵敏度配置" tooltip="取值范围：1~10，0~3bit；数值越大，RVC制动越积极、频繁" />}
            name="sensitivityRvc"
          >
            <InputNumber placeholder="请输入RVC功能灵敏度配置" style={{ width: '100%' }} precision={0} min={1} max={10} />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
});

export default AebFunctionSwitchWriteForm;
